import { InfluxResponse } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/shared'
import { API } from '@mv-submodules/inplant-plantanalysis-fe-iblu/redux/actions'
import { FetchPromise } from '@mv-submodules/mvlabs-components-fe/functions/hooks/useGenericFetchHook'
import {
  GetSpeedAbsorptionRatioSummaryProps,
  Period,
  SpeedAbsorptionRatioSummaryData,
  SpeedAbsorptionRatioValues,
} from './_types'

/**
 * The base name for the InfluxDB measurement for speed absorption ratio.
 */
const SPEED_ABSORPTION_RATIO_MEASURE = 'SPEED_ABSORPTION_RATIO'

/**
 * Builds an InfluxDB query to get the latest speed absorption ratio summary for a given asset and period.
 * @param assetId - The ID of the asset.
 * @param period - The time period for the summary.
 * @returns The InfluxDB query string.
 */
const _buildQuery = (assetId: string, period: Period) => {
  return `SELECT * FROM "${assetId}_${SPEED_ABSORPTION_RATIO_MEASURE}_${period}" ORDER BY time DESC LIMIT 1`
}

/**
 * Parses the JSON response from an InfluxDB query and extracts the speed absorption ratio values.
 * @param response - The raw JSON string response from the API.
 * @returns An object containing the speed absorption ratio values, or null if parsing fails or data is absent.
 */
const _parseSpeedAbsorptionRatioValuesFromResponse = (response: string | null): SpeedAbsorptionRatioValues | null => {
  if (response == null) {
    return null
  }
  try {
    const parsedResponse = JSON.parse(response) as InfluxResponse
    /**
     * 1 => mean_absorption
     * 2 => mean_speed
     * 3 => speed_absorption_ratio
     */
    const values = parsedResponse.results[0].series?.[0]?.values[0].slice(1, 4)
    const parsedValues = values?.map(value => {
      if (typeof value === 'number') {
        return value
      }
      return null
    })

    if (parsedValues) {
      return {
        meanAbsorption: parsedValues?.[0] ?? null,
        meanSpeed: parsedValues?.[1] ?? null,
        speedAbsorptionRatio: parsedValues?.[2] ?? null,
      }
    }
    return null
  } catch {
    return null
  }
}

interface Options {
  abortController: AbortController
}

/**
 * Fetches the speed and absorption ratio summary data for a specific asset across multiple time periods.
 * It makes parallel API requests for each period and aggregates the results.
 * @param props - The properties required to fetch the summary data, including assetId, plant, and a list of periods.
 * @returns A fetch promise mapping each period to its corresponding summary data.
 */
const getSpeedAbsorptionRatioSummary: FetchPromise<
  SpeedAbsorptionRatioSummaryData,
  [GetSpeedAbsorptionRatioSummaryProps, Options]
> = (props: GetSpeedAbsorptionRatioSummaryProps, options: Options) => async (
  _dispatch
): Promise<SpeedAbsorptionRatioSummaryData> => {
  const { assetId, plant, periods } = props

  const plantQueryParam = `plant=${encodeURIComponent(plant)}`

  const promises = periods.map<Promise<string | null>>(period => {
    const query = _buildQuery(assetId, period)
    const encodedQuery = encodeURIComponent(query)
    return (
      API()
        .request(`/query?${plantQueryParam}&q=${encodedQuery}`, { signal: options.abortController.signal })
        // Ensure individual request failures do not reject the whole batch
        .catch(() => null)
    )
  })

  const responses = await Promise.all(promises)

  /**
   * Aggregates the responses into a summary object by mapping each period to its parsed data.
   * Each response is processed to extract speed absorption ratio values using the parser function.
   */
  const summary: SpeedAbsorptionRatioSummaryData = responses.reduce((acc, response, index) => {
    const period = periods[index]
    acc[period] = _parseSpeedAbsorptionRatioValuesFromResponse(response)
    return acc
  }, {} as SpeedAbsorptionRatioSummaryData)

  // Always return an object so consumers can treat "no data" as a valid success state.
  return summary
}

export { getSpeedAbsorptionRatioSummary }
